import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CustomerDish, RestaurantPage } from '@capsico/contracts';
import { errorStatus } from '../api/client';
import { fetchRestaurant } from '../api/discover';
import { track } from '../api/track';
import { pointKey, type DeliveryPoint } from './useDeliveryPoint';

/** One row of the menu list: a category header or a dish (flat, so long menus can be windowed). */
export type MenuRow =
  | { kind: 'category'; key: string; id: string; name: string; count: number }
  | { kind: 'dish'; key: string; dish: CustomerDish };

/**
 * A restaurant page for the delivery point: header, licence, deliverability and the menu with
 * a veg-only switch and in-menu search. Menus with many dishes are flattened into rows.
 */
export function useRestaurant(outletId: string, point: DeliveryPoint | null) {
  const [vegOnly, setVegOnly] = useState(false);
  const [text, setText] = useState('');
  const [open, setOpen] = useState<CustomerDish | null>(null);

  const query = useQuery({
    queryKey: ['restaurant', outletId, pointKey(point)],
    queryFn: () =>
      fetchRestaurant(outletId, {
        lat: (point as DeliveryPoint).lat,
        lng: (point as DeliveryPoint).lng,
      }),
    enabled: point !== null,
    staleTime: 30_000,
    retry: (count, error) => errorStatus(error) !== 404 && count < 2,
  });
  const page: RestaurantPage | null = query.data ?? null;

  /* W19: one restaurant_view per visit, plus menu_view when the menu has dishes to browse. */
  const viewedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!page || viewedFor.current === page.outlet.outletId) return;
    viewedFor.current = page.outlet.outletId;
    const fields = {
      country: page.outlet.country,
      townId: page.area.town?.id ?? null,
      outletId: page.outlet.outletId,
    };
    track('restaurant_view', fields);
    const dishes = page.menu.categories.reduce((sum, category) => sum + category.items.length, 0);
    if (dishes > 0) track('menu_view', { ...fields, props: { dishes } });
  }, [page]);

  const rows = useMemo<MenuRow[]>(() => {
    if (!page) return [];
    const needle = text.trim().toLowerCase();
    const out: MenuRow[] = [];
    for (const category of page.menu.categories) {
      const dishes = category.items.filter(
        (dish) =>
          (!vegOnly || dish.foodType === 'veg') &&
          (!needle ||
            dish.name.toLowerCase().includes(needle) ||
            (dish.description ?? '').toLowerCase().includes(needle)),
      );
      if (dishes.length === 0) continue;
      out.push({
        kind: 'category',
        key: `c-${category.id}`,
        id: category.id,
        name: category.name,
        count: dishes.length,
      });
      for (const dish of dishes) out.push({ kind: 'dish', key: `d-${dish.id}`, dish });
    }
    return out;
  }, [page, vegOnly, text]);

  return {
    loading: query.isLoading,
    notFound: errorStatus(query.error) === 404,
    failed: query.isError && errorStatus(query.error) !== 404,
    retry: () => void query.refetch(),
    page,
    rows,
    dishCount: rows.filter((row) => row.kind === 'dish').length,
    vegOnly,
    setVegOnly,
    text,
    setText,
    open,
    openDish: (dish: CustomerDish) => setOpen(dish),
    closeDish: () => setOpen(null),
  };
}
